/**
 * CartStateService — v3 port of the SPFx `CartStateService`.
 *
 * Source of truth (read-only):
 *   C:/Projects/retail-account-opening/spfx-account-opening-v2/.../services/CartStateService.ts
 *
 * Differences from the SPFx version (all intentional):
 *   1. No raw fetch + `x-functions-key` — every call is routed through
 *      `@retail-ops/api-client` so the W3C traceparent + x-pcb-* headers +
 *      abort wiring stay consistent with the rest of the app.
 *   2. Return shape reuses `CartStateResult` from shared-types so React code
 *      can just forward the envelope without re-shaping it.
 *   3. Typed against the SQL-backed `CartDto` instead of the legacy stringified
 *      `cartDataJson` column. The stringified payloads are still passed
 *      through unchanged so an SPFx-generated cart row can still be hydrated.
 *   4. `listMy` + `listUnclaimed` currently return 501 — exposed here as
 *      regular methods so calling UI degrades gracefully when the Function
 *      ships those routes.
 *   5. Fail-soft at callsite, same as SPFx: any transport error resolves to
 *      `{ success: false, error: ... }` instead of throwing.
 *
 * Line-for-line SPFx parity reference:
 *   createCart        ↔ v3.carts.create                 POST   /v3/carts
 *   getCart           ↔ v3.carts.get                    GET    /v3/carts/{id}
 *   updateCart        ↔ v3.carts.update                 PATCH  /v3/carts/{id}
 *  (v3 additions that were inline in SPFx)
 *   listMy            ↔ v3.carts.listMy                 GET    /v3/carts/my
 *   listUnclaimed     ↔ v3.carts.listUnclaimed          GET    /v3/carts/unclaimed
 *   claim             ↔ v3.carts.claim                  POST   /v3/carts/{id}/claim
 *   unclaim           ↔ v3.carts.unclaim                POST   /v3/carts/{id}/unclaim
 *   close             ↔ v3.carts.close                  POST   /v3/carts/{id}/archive (reason=Completed)
 *   abandon           ↔ v3.carts.abandon                POST   /v3/carts/{id}/archive (reason=Abandoned)
 */

import type { ApiClient } from '@retail-ops/api-client';
import type {
  CartDto,
  CartStateResult,
  CreateCartPayload,
  UpdateCartPayload,
  CartListFilters,
  CartClaimInput,
} from '@retail-ops/shared-types';

export interface CartStateServiceResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  errorCode?: string;
}

export type CartStateServiceResponse = CartStateServiceResult<CartDto>;

/** Optional per-call context — mirrors SPFx's AbortSignal + debug hooks. */
export interface CartStateServiceOptions {
  signal?: AbortSignal;
  /** Hook for structured logging — keeps the v3 port quiet by default. */
  log?: (event: 'request' | 'response' | 'error', detail: string) => void;
}

function toResult<T>(res: { success: boolean; cart?: T; error?: string; errorCode?: string }): CartStateServiceResult<T> {
  if (res.success && res.cart) {
    return { success: true, data: res.cart };
  }
  const errorCode = res.errorCode;
  return {
    success: false,
    error: res.error ?? 'cart operation failed',
    ...(errorCode !== undefined ? { errorCode } : {}),
  };
}

/**
 * Service class — instantiate once per ApiClient (the CartProvider keeps a
 * singleton in a `useMemo`). All methods are thin wrappers so React callers
 * can depend on a stable shape without caring about the underlying http client.
 */
export class CartStateService {
  public constructor(private readonly api: ApiClient) {}

  // ─── Create / fetch / update ──────────────────────────────────────────────

  public async createCart(
    payload: CreateCartPayload,
    opts: CartStateServiceOptions = {},
  ): Promise<CartStateServiceResponse> {
    opts.log?.('request', `POST /v3/carts officer=${payload.officerCode} branch=${payload.branch}`);
    try {
      const res = await this.api.retail.carts.create(payload, opts.signal ? { signal: opts.signal } : undefined);
      opts.log?.('response', `POST /v3/carts → ${res.success ? 'ok' : 'fail'}`);
      return toResult<CartDto>(res as CartStateResult);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      opts.log?.('error', `POST /v3/carts: ${msg}`);
      return { success: false, error: msg };
    }
  }

  public async getCart(
    cartId: string,
    opts: CartStateServiceOptions = {},
  ): Promise<CartStateServiceResponse> {
    opts.log?.('request', `GET /v3/carts/${cartId}`);
    try {
      const res = await this.api.retail.carts.get(cartId, opts.signal ? { signal: opts.signal } : undefined);
      opts.log?.('response', `GET /v3/carts/${cartId} → ${res.success ? 'ok' : 'fail'}`);
      return toResult<CartDto>(res as CartStateResult);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      opts.log?.('error', `GET /v3/carts/${cartId}: ${msg}`);
      return { success: false, error: msg };
    }
  }

  /**
   * Optimistic-concurrency PATCH. Callers must pass `expectedVersion` — if the
   * server rejects with 409 the returned envelope carries `errorCode='cart-version-conflict'`
   * and the caller is expected to `getCart()` again and rebase.
   */
  public async updateCart(
    cartId: string,
    patch: UpdateCartPayload,
    opts: CartStateServiceOptions = {},
  ): Promise<CartStateServiceResponse> {
    opts.log?.('request', `PATCH /v3/carts/${cartId} keys=${Object.keys(patch).join(',')}`);
    try {
      const res = await this.api.retail.carts.update(cartId, patch, opts.signal ? { signal: opts.signal } : undefined);
      opts.log?.('response', `PATCH /v3/carts/${cartId} → ${res.success ? 'ok' : res.errorCode ?? 'fail'}`);
      return toResult<CartDto>(res as CartStateResult);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      opts.log?.('error', `PATCH /v3/carts/${cartId}: ${msg}`);
      return { success: false, error: msg };
    }
  }

  // ─── List / claim / unclaim / close / abandon ─────────────────────────────

  public async listMy(
    filters: CartListFilters = {},
    opts: CartStateServiceOptions = {},
  ): Promise<CartStateServiceResult<ReadonlyArray<CartDto>>> {
    try {
      const res = await this.api.retail.carts.listMy(filters, opts.signal ? { signal: opts.signal } : undefined);
      // api-client returns { success, data, error } for list-shape routes.
      const envelope = res as unknown as { success?: boolean; data?: CartDto[]; error?: string };
      if (envelope?.success === false) {
        return { success: false, error: envelope.error ?? 'listMy unavailable' };
      }
      return { success: true, data: (envelope.data ?? []) as ReadonlyArray<CartDto> };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      return { success: false, error: msg };
    }
  }

  public async listUnclaimed(
    filters: CartListFilters = {},
    opts: CartStateServiceOptions = {},
  ): Promise<CartStateServiceResult<ReadonlyArray<CartDto>>> {
    try {
      const res = await this.api.retail.carts.listUnclaimed(filters, opts.signal ? { signal: opts.signal } : undefined);
      const envelope = res as unknown as { success?: boolean; data?: CartDto[]; error?: string };
      if (envelope?.success === false) {
        return { success: false, error: envelope.error ?? 'listUnclaimed unavailable' };
      }
      return { success: true, data: (envelope.data ?? []) as ReadonlyArray<CartDto> };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      return { success: false, error: msg };
    }
  }

  public async claim(
    cartId: string,
    req: CartClaimInput,
    opts: CartStateServiceOptions = {},
  ): Promise<CartStateServiceResponse> {
    try {
      const res = await this.api.retail.carts.claim(cartId, req, opts.signal ? { signal: opts.signal } : undefined);
      return toResult<CartDto>(res as CartStateResult);
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : String(err) };
    }
  }

  public async unclaim(
    cartId: string,
    opts: CartStateServiceOptions = {},
  ): Promise<CartStateServiceResponse> {
    try {
      const res = await this.api.retail.carts.unclaim(cartId, opts.signal ? { signal: opts.signal } : undefined);
      return toResult<CartDto>(res as CartStateResult);
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : String(err) };
    }
  }

  /**
   * Reassign the cart to another officer for a clean handoff. Server-gated to
   * Supervisor / Manager / Admin; officer code and branch derive from the
   * RECEIVING officer's record, never from this call.
   */
  public async reassign(
    cartId: string,
    toOfficerCode: string,
    reason?: string,
    opts: CartStateServiceOptions = {},
  ): Promise<CartStateServiceResponse> {
    try {
      const res = await this.api.retail.carts.reassign(
        cartId,
        { toOfficerCode, ...(reason ? { reason } : {}) },
        opts.signal ? { signal: opts.signal } : undefined,
      );
      return toResult<CartDto>(res as CartStateResult);
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : String(err) };
    }
  }

  /** Close = archive with reason='Completed'. Terminal happy-path. */
  public async close(
    cartId: string,
    details?: string,
    opts: CartStateServiceOptions = {},
  ): Promise<CartStateServiceResponse> {
    try {
      const res = await this.api.retail.carts.archive(
        cartId,
        details ? { reason: 'Completed', details } : { reason: 'Completed' },
        opts.signal ? { signal: opts.signal } : undefined,
      );
      return toResult<CartDto>(res as CartStateResult);
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : String(err) };
    }
  }

  /** Abandon = archive with reason='Abandoned'. Populates Kill List. */
  public async abandon(
    cartId: string,
    details?: string,
    opts: CartStateServiceOptions = {},
  ): Promise<CartStateServiceResponse> {
    try {
      const res = await this.api.retail.carts.archive(
        cartId,
        details ? { reason: 'Abandoned', details } : { reason: 'Abandoned' },
        opts.signal ? { signal: opts.signal } : undefined,
      );
      return toResult<CartDto>(res as CartStateResult);
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : String(err) };
    }
  }
}
